// New approval gate for the "General Invoice Engine" (ARSL/ATL/AE/ES,
// src/components/Dashboard.js — previously PDF-download-only, no Firestore
// record at all). Each upload produces a "service" invoice (taxable) and a
// "reimbursement" invoice (pure cost passthrough) per segment; each is its
// own invoice number and is approved/snapshotted independently.
import { collection, doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";
import { getActiveProfileInTransaction } from "../services/financeCore/clientTaxProfileService";
import { buildSnapshotFields, snapshotDocRef } from "../services/financeCore/invoiceSnapshotService";
import { parseMonthKey } from "../services/financeCore/monthKey";
import { assertPeriodOpen } from "../services/financeCore/periodLockService";
import { InvoiceOverrideRequiredError } from "../services/financeCore/invoiceCorrection";
import { GSOLAR_NUMBER_PATTERN, checkGsolarSequence, siblingInvoiceNumber } from "./gsolarNumbering";

// "AE" -> "AESL", "ATL - General" -> "ATL", "ARSL - North" -> "ARSL".
// The legacy invoice-backend builder (aeBuilder.js) still emits "AE" as the
// raw company code — that's tied to real employee-ID prefixes and filename
// detection upstream and is left alone. This alias is the one place that
// raw code gets normalized to "AESL", the client code used everywhere in the
// finance layer (tax profiles, receivables, P&L), so Allied Engineering has
// exactly one identity there instead of two.
const CLIENT_CODE_ALIASES = { AE: "AESL" };

export function deriveGeneralClientCode(company) {
  const raw = String(company || "").split(/[\s-]+/)[0].trim().toUpperCase();
  return CLIENT_CODE_ALIASES[raw] || raw;
}

// Thrown when the same invoice number/month/date is being re-approved but
// the computed amount has genuinely changed (e.g. a formula fix recalculated
// the figures) — distinct from a harmless duplicate click, which is a silent
// no-op. The caller (approval UI) catches this, shows the before/after
// amounts, and re-calls approveGeneralInvoice with { force: true,
// correctionReason } once the operator confirms the override.
export { InvoiceOverrideRequiredError };

// invoiceDate defaults to today but should be passed explicitly (e.g. from a
// date field in the approval UI) when catching up on a past month, so
// receivables aging is computed off the real invoice date, not today's.
export async function approveGeneralInvoice(invoice, invoiceMonth, invoiceDate, { force = false, correctionReason = "" } = {}) {
  const invoiceNumber = String(invoice?.invoiceNumber || "").trim();
  if (!invoiceNumber) throw new Error("This invoice has no invoice number yet — generate invoices first.");
  const monthKey = parseMonthKey(invoiceMonth);
  if (!monthKey) throw new Error("Enter a valid invoice month (e.g. Feb-2027) before approving.");
  const clientCode = deriveGeneralClientCode(invoice.company);
  const grandTotal = Number(invoice.grandTotal || invoice.summary?.grandTotal || 0);
  if (!(grandTotal > 0)) throw new Error("The invoice total must be greater than zero.");
  const effectiveInvoiceDate = invoiceDate || new Date().toISOString().slice(0, 10);

  const registryId = encodeURIComponent(invoiceNumber.toUpperCase());
  const snapshotPayload = {
    invoiceNumber,
    clientCode,
    company: invoice.company,
    invoiceType: invoice.type,
    monthKey,
    invoiceDate: effectiveInvoiceDate,
    grandTotal,
    status: "APPROVED",
    approvedBy: auth.currentUser?.uid || null,
    approvedByEmail: auth.currentUser?.email || null
  };

  // GSolar Power / VS Alpha's "{yy}-{seq}/GSP-or-VS/ALM" number is only
  // provisional until now (see peekNextGSolarInvoiceNumber in
  // invoiceController.js) — regenerating or re-uploading a batch before
  // approving never burns a real number; the shared invoiceCounters/
  // GSOLAR_{yy} doc is only actually reserved here, at approval, gap-checked
  // the same way IIL's own numbering is. A counter that doesn't exist yet
  // means this is the very first G-Solar invoice in the system — any
  // manually-typed starting number is accepted as the seed (e.g. to continue
  // an existing paper-based series) rather than forcing 0001.
  const gsolarMatch = (clientCode === "G_SOLAR" || clientCode === "VS_ALPHA")
    ? invoiceNumber.toUpperCase().match(GSOLAR_NUMBER_PATTERN)
    : null;

  let correctionRecord = null;

  await assertPeriodOpen(monthKey);
  await runTransaction(db, async (transaction) => {
    const registryRef = doc(db, "general_invoice_registry", registryId);
    const registryDoc = await transaction.get(registryRef);
    if (registryDoc.exists() && registryDoc.data().monthKey !== monthKey) {
      throw new Error(`${invoiceNumber} is already approved for ${registryDoc.data().monthKey}.`);
    }
    if (registryDoc.exists() && registryDoc.data().status === "APPROVED") {
      const existingAmount = Number(registryDoc.data().grandTotal || 0);
      // A genuinely identical re-click (e.g. after re-uploading the same
      // batch) is a safe no-op — writing again would try to overwrite the
      // immutable approved_invoice_snapshots doc for no reason. Only a real
      // amount change needs the explicit, warned override path below.
      if (existingAmount === grandTotal) {
        return;
      }
      if (!force) {
        throw new InvoiceOverrideRequiredError({
          invoiceNumber,
          existingAmount,
          newAmount: grandTotal,
          existingApprovedAt: registryDoc.data().approvedAt || null
        });
      }
      if (!correctionReason.trim()) {
        throw new Error("A reason is required to override an already-approved invoice.");
      }
      correctionRecord = {
        invoiceNumber,
        clientCode,
        monthKey,
        previousAmount: existingAmount,
        newAmount: grandTotal,
        reason: correctionReason.trim(),
        correctedBy: auth.currentUser?.uid || null,
        correctedByEmail: auth.currentUser?.email || null,
        correctedAt: serverTimestamp()
      };
    }

    // Firestore transactions require every read to happen before any write,
    // so the GSolar counter check (a read) and getActiveProfileInTransaction
    // (also a read) both run here, before any transaction.set() call below.
    let gsolarCounterRef = null;
    let gsolarCounterWrite = null;
    if (gsolarMatch && !registryDoc.exists()) {
      const [, yy, sequenceText] = gsolarMatch;
      gsolarCounterRef = doc(db, "invoiceCounters", `GSOLAR_${yy}`);
      // G-Solar and VS Alpha share one number (27-0007/GSP and 27-0007/VS are a pair), so the
      // second company's invoice is accepted once its sibling has been approved.
      const [counterDoc, siblingDoc] = await Promise.all([
        transaction.get(gsolarCounterRef),
        transaction.get(doc(db, "general_invoice_registry", encodeURIComponent(siblingInvoiceNumber(invoiceNumber))))
      ]);
      const currentLast = counterDoc.exists() ? Number(counterDoc.data().lastNumber || 0) : 0;
      const sequence = Number(sequenceText);
      const problem = checkGsolarSequence({ currentLast, sequence, yy, siblingExists: siblingDoc.exists() && siblingDoc.data().status === "APPROVED" });
      if (problem) throw new Error(problem);
      gsolarCounterWrite = { lastNumber: Math.max(currentLast, sequence), year: yy };
    }

    const taxProfile = await getActiveProfileInTransaction(transaction, clientCode);
    const finalInvoiceDate = registryDoc.exists() ? registryDoc.data().invoiceDate : effectiveInvoiceDate;
    const snapshotFields = buildSnapshotFields({
      clientCode,
      sourceCollection: "general_invoice_registry",
      sourceDocId: registryId,
      invoiceNumber,
      monthKey,
      accountingPeriod: monthKey,
      invoiceDate: finalInvoiceDate,
      taxProfile,
      sourceDoc: invoice
    });
    // Marks this write as an authorized correction, not a routine approval —
    // the Firestore rule for approved_invoice_snapshots only allows updating
    // an already-approved (non-manual) snapshot when this is present, so an
    // ordinary re-approval attempt still can't silently overwrite one.
    if (correctionRecord) snapshotFields.correctionReason = correctionRecord.reason;

    if (gsolarCounterRef) {
      transaction.set(gsolarCounterRef, gsolarCounterWrite, { merge: true });
    }
    transaction.set(registryRef, {
      ...snapshotPayload,
      invoiceDate: finalInvoiceDate,
      approvedAt: registryDoc.exists() ? registryDoc.data().approvedAt : serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });
    if (correctionRecord) {
      transaction.set(snapshotDocRef(registryId), snapshotFields, { merge: true });
      transaction.set(doc(collection(db, "invoice_snapshot_corrections")), correctionRecord);
    } else {
      transaction.set(snapshotDocRef(registryId), snapshotFields);
    }
  });

  return snapshotPayload;
}
